/**
 * 响应式断点检测 Hook
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * 默认断点值 (像素)
 */
const DEFAULT_BREAKPOINTS = {
  mobile: 576,
  tablet: 768,
  desktop: 1200,
} as const;

/**
 * 断点配置
 */
export interface Breakpoints {
  /** 移动端最大宽度，默认 576 */
  mobile?: number;
  /** 平板最大宽度，默认 768 */
  tablet?: number;
  /** 桌面端最小宽度，默认 1200 */
  desktop?: number;
}

/**
 * 响应式状态
 */
interface ResponsiveState {
  /** 是否为移动端 (宽度 < mobile) */
  isMobile: boolean;
  /** 是否为平板 (mobile <= 宽度 < desktop) */
  isTablet: boolean;
  /** 是否为桌面端 (宽度 >= desktop) */
  isDesktop: boolean;
  /** 当前视口宽度 */
  width: number;
}

/**
 * 响应式断点检测 Hook
 *
 * 监听窗口大小变化，返回当前设备类型。
 *
 * @param breakpoints - 自定义断点值
 * @returns 响应式状态
 *
 * @example
 * ```tsx
 * const { isMobile, isTablet, isDesktop } = useResponsive();
 *
 * return isMobile ? <MobileLayout /> : <DesktopLayout />;
 * ```
 */
export function useResponsive(breakpoints?: Breakpoints): ResponsiveState {
  const bp = useMemo(
    () => ({
      mobile: breakpoints?.mobile ?? DEFAULT_BREAKPOINTS.mobile,
      tablet: breakpoints?.tablet ?? DEFAULT_BREAKPOINTS.tablet,
      desktop: breakpoints?.desktop ?? DEFAULT_BREAKPOINTS.desktop,
    }),
    [breakpoints?.mobile, breakpoints?.tablet, breakpoints?.desktop],
  );

  const getState = useCallback((): ResponsiveState => {
    if (typeof window === 'undefined') {
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        width: 1920,
      };
    }

    const width = window.innerWidth;
    return {
      isMobile: width < bp.mobile,
      isTablet: width >= bp.mobile && width < bp.desktop,
      isDesktop: width >= bp.desktop,
      width,
    };
  }, [bp.mobile, bp.desktop]);

  const [state, setState] = useState<ResponsiveState>(getState);

  useEffect(() => {
    let rafId: number | null = null;

    const handleResize = () => {
      // 使用 rAF 节流：每帧最多计算一次，避免高频 resize 事件导致性能问题
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        setState(getState());
      });
    };

    window.addEventListener('resize', handleResize);
    // 初始化时同步更新一次
    setState(getState());

    return () => {
      window.removeEventListener('resize', handleResize);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [getState]);

  return state;
}
